/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-explicit-any */
import cloneDeep from 'lodash-es/cloneDeep';
import {
  TParsedPalette,
  isColorDefinition,
  IActionOpacity,
  IColorDefinition,
  TCustomPalette,
} from '../types';
import { defaultLighten, defaultDarken, defaultGetContrastText } from '../util';
import applyStatesGetColor from './applyStatesGetColor';
import buildStateObject from './buildStateObject';
import getColorsAndStatesByPath from './getColorsAndStatesByPath';
import getColorState from './getColorState';
import getColorStates from './getColorStates';
import getOneColorState from './getOneColorState';
import buildColorsObject from './buildColorsObject';
import applyStates from './applyStates';
import getColorsAndStatesByDefinition from './getColorsAndStatesByDefinition';
import { getSelectedColors } from '..';
import getColorsByPath from './getColorsByPath';

function paletteFunction(callback: (...args: any[]) => unknown): any {
  return (...args: any[]) => {
    const firstParameter = args[0];
    let isFirstParameterATheme = false;

    const themeProps = [
      'fontSizes',
      'breakpoints',
      'fonts',
      'lineHeights',
      'letterSpacings',
      'radii',
      'zIndices',
      'useRootStyles',
    ];
    /**
     * In this block, I must decide if the first argument is a theme or
     * not
     */
    if (firstParameter instanceof Object && firstParameter !== null) {
      const firstParameterKeys = Object.keys(firstParameter);
      for (const key of firstParameterKeys) {
        if (themeProps.includes(key)) {
          isFirstParameterATheme = true;
          break;
        }
      }
    }

    if (isFirstParameterATheme) {
      /** Do nothing */
    } else {
      return callback(...args);
    }
  };
}

export default function parsePalette(palette: TCustomPalette) {
  const newPalette: TParsedPalette = cloneDeep(palette) as TParsedPalette;

  // Put methods
  newPalette.lightenColor = paletteFunction(
    palette.lightenColor ??
      ((color: string, ratio?: number) => {
        return defaultLighten(color, ratio ?? palette.lightenRatio);
      }),
  );
  newPalette.darkenColor = paletteFunction(
    palette.darkenColor ??
      ((color: string, ratio?: number) => {
        return defaultDarken(color, ratio ?? palette.darkenRatio);
      }),
  );
  newPalette.getContrastText = paletteFunction(
    palette.getContrastText ?? defaultGetContrastText,
  );

  // Fill colors
  Object.entries(palette).forEach(([key, value]) => {
    const castedValue = value as string | number | IColorDefinition;
    if (isColorDefinition(castedValue)) {
      (newPalette as Record<string, unknown>)[key] = {
        main: castedValue.main,
        light: castedValue.light ?? newPalette.lightenColor(castedValue.main),
        dark: castedValue.dark ?? newPalette.darkenColor(castedValue.main),
        contrastText:
          castedValue.contrastText ??
          newPalette.getContrastText(castedValue.main),
      };
    } else {
      (newPalette as Record<string, unknown>)[key] = value;
    }
  });

  // Draw borders
  newPalette.border.article =
    newPalette.border.article ?? newPalette.secondary.main;
  newPalette.border.field = newPalette.border.field ?? newPalette.text.primary;
  newPalette.border.section =
    newPalette.border.section ?? newPalette.primary.main;

  // Define actions
  function parseAction(action: string | IActionOpacity): IActionOpacity {
    if (typeof action === 'string')
      return {
        dark: action,
        light: action,
      };
    return {
      dark: action.dark ?? action.light,
      light: action.light ?? action.dark,
    };
  }

  newPalette.action.active = parseAction(newPalette.action.active);
  newPalette.action.disabled = parseAction(newPalette.action.disabled);
  newPalette.action.focus = parseAction(newPalette.action.focus);
  newPalette.action.hover = parseAction(newPalette.action.hover);
  newPalette.action.readonly = parseAction(newPalette.action.readonly);
  newPalette.action.selected = parseAction(newPalette.action.selected);

  newPalette.gray = newPalette.gray ?? {};
  for (let i = 1; i <= 18; i++) {
    const index = Math.round(((255 - 255 / 18) / 18) * i);
    newPalette.gray[(i * 50) as keyof typeof newPalette.gray] =
      newPalette.gray[(i * 50) as keyof typeof newPalette.gray] ??
      `rgb(${index},${index},${index})`;
  }

  newPalette.applyStates = paletteFunction(applyStates);
  newPalette.buildColorsObject = paletteFunction(buildColorsObject);
  newPalette.buildStateObject = paletteFunction(buildStateObject);
  newPalette.getColor = paletteFunction((color, state) =>
    getOneColorState(color, state, newPalette),
  );
  newPalette.getStatesForColors = paletteFunction((definition, states) =>
    applyStatesGetColor(definition, newPalette, states),
  );
  newPalette.getOneState = paletteFunction((definition, state) =>
    getColorState(definition, state, newPalette),
  );
  newPalette.getColorStates = paletteFunction((definition, state) =>
    getColorStates(definition, newPalette, state),
  );
  newPalette.getStatesFromDefinition = paletteFunction((definition, states) =>
    getColorsAndStatesByDefinition(definition, newPalette, states),
  );
  newPalette.getStatesFromPath = paletteFunction(getColorsAndStatesByPath);
  newPalette.getSelectedColors = paletteFunction(() =>
    getSelectedColors(newPalette),
  );
  newPalette.getColorsByPath = paletteFunction(getColorsByPath);
  return newPalette;
}
